import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import ShareStrip from "@/components/common/ShareStrip";
import MediaUploader from "@/components/media/MediaUploader";
import {
  X,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Loader2,
  Calendar,
  User,
  Mail,
  Phone,
  Mic2,
  UserPlus,
  Users,
  ExternalLink,
  AlertTriangle,
  Facebook,
  Instagram,
  Youtube,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORMS = [
  {
    key: "facebook",
    name: "Facebook",
    icon: Facebook,
    color: "#1877F2",
    actions: [
      { key: "follow_spotlight", label: "Follow SB Spotlight page", icon: UserPlus },
      { key: "follow_sbmedia", label: "Follow SB Media Studio page", icon: UserPlus },
      {
        key: "invite",
        label: "Invite ALL your contacts to follow",
        icon: Users,
        critical: true,
      },
    ],
  },
  {
    key: "instagram",
    name: "Instagram",
    icon: Instagram,
    color: "#E1306C",
    actions: [
      { key: "follow", label: "Follow @s.b.spotlight", icon: UserPlus },
    ],
  },
  {
    key: "tiktok",
    name: "TikTok",
    icon: Mic2,
    color: "#69C9D0",
    actions: [
      { key: "follow", label: "Follow @smallbusinesspotlight", icon: UserPlus },
    ],
  },
  {
    key: "youtube",
    name: "YouTube",
    icon: Youtube,
    color: "#FF0000",
    actions: [
      { key: "subscribe", label: "Subscribe to @SB_Spotlight", icon: UserPlus },
    ],
  },
];

const STEPS = ["Platforms", "Actions", "Date", "Contact", "Done"];

const empty = {
  platforms: [],
  attestations: {},
  preferred_date: "",
  time_slot: "",
  name: "",
  email: "",
  phone: "",
  social_handle: "",
  project_brief: "",
};

const monthLabel = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

const PodcastGuestModal = ({ open, onClose }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [bookingId, setBookingId] = useState(null);
  // Track which guest_id to use for click-tracking; for new applications we
  // generate a temp id locally and reuse on the backend post-creation
  const [clientId] = useState(() =>
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `tmp-${Date.now()}`,
  );
  const [honeypot, setHoneypot] = useState(""); // bots often fill anything named "website"
  const openedAtRef = useRef(0);
  // Signed one-time upload token for the public media endpoint — issued
  // by the backend when the applicant enters a valid email on step 3
  // (Contact). No auth cookie required for guest applications.
  const [uploadToken, setUploadToken] = useState(null);
  const [uploadedMedia, setUploadedMedia] = useState([]);
  const emailForTokenRef = useRef("");

  useEffect(() => {
    if (open) {
      setStep(0);
      setForm(empty);
      setError("");
      setBookingId(null);
      setHoneypot("");
      setUploadToken(null);
      setUploadedMedia([]);
      emailForTokenRef.current = "";
      openedAtRef.current = Date.now();
    }
  }, [open]);

  // Fetch a signed upload token when the applicant lands on the Contact
  // step with a valid email — enables the optional headshot/logo picker.
  useEffect(() => {
    if (!open || step !== 3) return;
    const em = (form.email || "").trim().toLowerCase();
    if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) return;
    if (emailForTokenRef.current === em && uploadToken) return;
    emailForTokenRef.current = em;
    (async () => {
      try {
        const { data } = await axios.post(`${API}/media/public/token`, {
          owner_type: "guest_request",
          owner_id: em,
        });
        setUploadToken(data.token);
      } catch {
        // Token failure is non-blocking — form still submits without media.
      }
    })();
  }, [open, step, form.email, uploadToken]);

  // Lock body scroll while modal is open (critical for mobile)
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (open && step === 2 && slots.length === 0) {
      setLoadingSlots(true);
      axios
        .get(`${API}/podcast-slots/available?weeks=10`)
        .then(({ data }) => setSlots(data.saturdays || []))
        .catch(() => setError("Could not load available dates"))
        .finally(() => setLoadingSlots(false));
    }
  }, [open, step, slots.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const togglePlatform = (k) => {
    setForm((f) => {
      const has = f.platforms.includes(k);
      const platforms = has ? f.platforms.filter((p) => p !== k) : [...f.platforms, k];
      // If unchecking, drop those attestations
      const attestations = { ...f.attestations };
      if (has) delete attestations[k];
      return { ...f, platforms, attestations };
    });
  };

  const setAction = (plat, action, val) => {
    setForm((f) => ({
      ...f,
      attestations: {
        ...f.attestations,
        [plat]: { ...(f.attestations[plat] || {}), [action]: val },
      },
    }));
  };

  const trackedHref = (plat, action) =>
    `${API}/social-track/${clientId}/${plat}/${action}`;

  const platformsSelected = form.platforms.length > 0;
  const allActionsCompleted = useMemo(() => {
    if (form.platforms.length === 0) return false;
    return form.platforms.every((p) => {
      const platDef = PLATFORMS.find((x) => x.key === p);
      const attest = form.attestations[p] || {};
      return platDef.actions.every((a) => !!attest[a.key]);
    });
  }, [form.platforms, form.attestations]);

  const canAdvance = () => {
    if (step === 0) return platformsSelected;
    if (step === 1) return allActionsCompleted;
    if (step === 2) return !!form.preferred_date && !!form.time_slot;
    if (step === 3)
      return (
        form.name.trim().length > 1 &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) &&
        form.project_brief.trim().length >= 20
      );
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/podcast-guests`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        social_handle: form.social_handle.trim(),
        project_brief: form.project_brief.trim(),
        preferred_date: form.preferred_date,
        time_slot: form.time_slot,
        platforms: form.platforms,
        attestations: form.attestations,
        media_ids: uploadedMedia.map((m) => m.id),
        website: honeypot,
        form_ms: openedAtRef.current
          ? Date.now() - openedAtRef.current
          : undefined,
      });
      setBookingId(data.id);
      setStep(4);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Submission failed";
      setError(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="pg-modal"
        data-testid="podcast-guest-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[100] overflow-y-auto overscroll-contain bg-black/85 backdrop-blur-md"
      >
        <div
          className="min-h-full flex items-start justify-center px-3 py-6 sm:py-10"
          data-testid="podcast-modal-backdrop"
        >
          <motion.div
            initial={{ y: 30, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 30, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-2xl my-4 rounded-3xl bg-[#0a0612] border border-white/10 shadow-[0_30px_120px_rgba(255,0,85,0.18)]"
          >
          <div className="flex items-start justify-between gap-4 px-6 md:px-7 pt-7 pb-5 bg-[#0a0612] rounded-t-3xl">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#ff0055] mb-2">
                [ Apply · Small Business Spotlight ]
              </div>
              <h3 className="font-display font-black text-2xl tracking-tight">
                Free guest spot — let&apos;s help each other grow.
              </h3>
              <div className="text-[#a395c3] text-sm mt-1.5 leading-relaxed">
                Saturdays only · 12 PM or 5 PM. In exchange for the free
                episode, we ask every guest to follow our pages and (for
                Facebook) invite their friends — that keeps the lights on for
                the next guest.
              </div>
            </div>
            <ShareStrip campaign="podcast_guest" title="Podcast guest slot — SB Media Studio" text="Free guest spot on the Small Business Spotlight — Central FL founders only." href="/#spotlight" className="mr-1" />
            <button
              onClick={onClose}
              aria-label="Close"
              data-testid="podcast-modal-close"
              className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>

          {step < 4 && (
            <div className="px-6 md:px-7 pb-4">
              <div className="flex items-center gap-2">
                {STEPS.slice(0, 4).map((s, i) => (
                  <div key={s} className="flex items-center gap-2 flex-1">
                    <div
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        i <= step ? "bg-[#ff0055]" : "bg-white/10"
                      }`}
                    />
                  </div>
                ))}
              </div>
              <div className="mt-2 text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">
                Step {step + 1} of 4 · {STEPS[step]}
              </div>
            </div>
          )}

          <div className="px-6 md:px-7 pb-7">
            {step === 0 && (
              <div className="space-y-3" data-testid="pg-step-platforms">
                <div className="rounded-2xl bg-[#ff0055]/8 border border-[#ff0055]/25 p-4 text-sm text-white/85 leading-relaxed">
                  <strong className="text-white">Which platforms are you on?</strong>{" "}
                  Check only the ones you actually have an account on — we&apos;ll
                  walk you through one quick action per platform on the next
                  step. If you don&apos;t have a platform, leave it blank — no
                  penalty.
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  {PLATFORMS.map((p) => {
                    const checked = form.platforms.includes(p.key);
                    return (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => togglePlatform(p.key)}
                        data-testid={`pg-platform-${p.key}`}
                        className={`p-4 rounded-2xl border text-left transition-all ${
                          checked
                            ? "border-[#ff0055] bg-[#ff0055]/10"
                            : "border-white/10 bg-white/[0.03] hover:border-white/25"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <p.icon
                            size={20}
                            style={{ color: checked ? p.color : "rgba(255,255,255,0.5)" }}
                          />
                          <div
                            className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-colors ${
                              checked
                                ? "border-[#ff0055] bg-[#ff0055]"
                                : "border-white/30"
                            }`}
                          >
                            {checked && <CheckCircle2 size={14} className="text-white" />}
                          </div>
                        </div>
                        <div className="font-display font-semibold text-sm">{p.name}</div>
                        <div className="text-[11px] text-[#a395c3] mt-0.5">
                          {p.actions.length} action{p.actions.length > 1 ? "s" : ""}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5" data-testid="pg-step-actions">
                <div className="rounded-2xl bg-[#00f0ff]/8 border border-[#00f0ff]/25 p-4 text-sm text-white/85 leading-relaxed">
                  <strong className="text-white">Tap each button below.</strong>{" "}
                  Each one opens the platform in a new tab and marks the action
                  complete. Come back here when you&apos;ve done it — if you
                  haven&apos;t actually followed yet, just uncheck and try again.
                </div>
                {form.platforms.map((pkey) => {
                  const pdef = PLATFORMS.find((x) => x.key === pkey);
                  return (
                    <div
                      key={pkey}
                      data-testid={`pg-platform-actions-${pkey}`}
                      className="rounded-2xl bg-white/[0.03] border border-white/10 p-4"
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <pdef.icon size={18} style={{ color: pdef.color }} />
                        <div className="font-display font-bold text-sm">{pdef.name}</div>
                      </div>
                      <div className="space-y-2.5">
                        {pdef.actions.map((a) => {
                          const done = !!(form.attestations[pkey] || {})[a.key];
                          return (
                            <button
                              key={a.key}
                              type="button"
                              onClick={() => {
                                // Open the social URL in a new tab AND mark done
                                if (!done) {
                                  window.open(trackedHref(pkey, a.key), "_blank", "noopener");
                                }
                                setAction(pkey, a.key, !done);
                              }}
                              data-testid={`pg-action-${pkey}-${a.key}`}
                              className={`w-full flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all ${
                                done
                                  ? "bg-[#22c55e]/10 border-[#22c55e]/40"
                                  : "bg-white/[0.04] border-white/10 hover:border-[#00f0ff]/40 hover:bg-white/[0.06]"
                              }`}
                            >
                              <div
                                className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
                                  done ? "bg-[#22c55e] text-[#05050a]" : "bg-[#00f0ff]/15 text-[#00f0ff]"
                                }`}
                              >
                                {done ? <CheckCircle2 size={16} /> : <a.icon size={15} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-medium leading-tight">
                                  {a.label}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.18em] mt-1 flex items-center gap-1.5">
                                  {done ? (
                                    <span className="text-[#22c55e]">Done · click to undo</span>
                                  ) : (
                                    <span className="text-[#00f0ff]">
                                      Tap to open & mark done
                                    </span>
                                  )}
                                  {a.critical && !done && (
                                    <span className="text-[#ffb800]">
                                      · <AlertTriangle size={9} className="inline" /> Required
                                    </span>
                                  )}
                                </div>
                              </div>
                              {!done && (
                                <ExternalLink
                                  size={14}
                                  className="text-white/40 flex-shrink-0"
                                />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4" data-testid="pg-step-date">
                <div className="rounded-2xl bg-[#a78bfa]/10 border border-[#a78bfa]/25 p-4 text-sm text-white/85 leading-relaxed">
                  <strong className="text-white">Pick your Saturday slot.</strong>{" "}
                  Episodes record at <strong>12 PM</strong> or <strong>5 PM</strong> only.
                  Greyed-out slots are already booked.
                </div>
                {loadingSlots ? (
                  <div className="py-12 flex items-center justify-center">
                    <Loader2 className="animate-spin text-[#00f0ff]" />
                  </div>
                ) : (
                  <div className="space-y-2.5">
                    {slots.map((sat) => {
                      const bothTaken = sat.slots.every((s) => s.taken);
                      return (
                        <div
                          key={sat.date}
                          data-testid={`pg-sat-${sat.date}`}
                          className={`rounded-2xl border p-4 ${
                            bothTaken
                              ? "border-white/5 bg-white/[0.01] opacity-50"
                              : "border-white/10 bg-white/[0.03]"
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-3">
                            <Calendar size={14} className="text-[#00f0ff]" />
                            <div className="font-display font-semibold text-sm">
                              {monthLabel(sat.date)}
                            </div>
                            {bothTaken && (
                              <span className="ml-auto text-[10px] uppercase tracking-[0.22em] text-white/40">
                                Fully booked
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {sat.slots.map((s) => {
                              const selected =
                                form.preferred_date === sat.date &&
                                form.time_slot === s.time;
                              const disabled = s.taken;
                              return (
                                <button
                                  key={s.time}
                                  type="button"
                                  disabled={disabled}
                                  onClick={() =>
                                    setForm((f) => ({
                                      ...f,
                                      preferred_date: sat.date,
                                      time_slot: s.time,
                                    }))
                                  }
                                  data-testid={`pg-slot-${sat.date}-${s.time}`}
                                  className={`py-3 rounded-xl text-sm font-display font-bold transition-all ${
                                    disabled
                                      ? "bg-white/[0.02] text-white/25 border border-white/5 cursor-not-allowed"
                                      : selected
                                        ? "bg-[#ff0055] text-white shadow-[0_0_22px_rgba(255,0,85,0.35)]"
                                        : "bg-white/[0.05] border border-white/15 hover:bg-white/[0.12] hover:border-[#ff0055]/40"
                                  }`}
                                >
                                  {s.time === "12:00" ? "12 PM" : "5 PM"}
                                  {disabled && (
                                    <div className="text-[9px] uppercase tracking-[0.18em] mt-0.5 opacity-60">
                                      Taken{s.holder_initials ? ` · ${s.holder_initials}` : ""}
                                    </div>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4" data-testid="pg-step-contact">
                <Field icon={User} label="Your name" required>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Jane Founder"
                    data-testid="pg-name"
                    className="w-full bg-transparent text-white outline-none text-sm placeholder:text-white/30"
                  />
                </Field>
                <Field icon={Mail} label="Email" required>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="you@brand.com"
                    data-testid="pg-email"
                    className="w-full bg-transparent text-white outline-none text-sm placeholder:text-white/30"
                  />
                </Field>
                <Field icon={Phone} label="Phone (optional)">
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    placeholder="+1 813 555 1212"
                    data-testid="pg-phone"
                    className="w-full bg-transparent text-white outline-none text-sm placeholder:text-white/30"
                  />
                </Field>
                <Field icon={Mic2} label="Your main social handle">
                  <input
                    value={form.social_handle}
                    onChange={(e) =>
                      setForm({ ...form, social_handle: e.target.value })
                    }
                    placeholder="@yourbrand · instagram.com/you"
                    data-testid="pg-handle"
                    className="w-full bg-transparent text-white outline-none text-sm placeholder:text-white/30"
                  />
                </Field>
                <Field icon={Mic2} label="Tell us about your business / story" required>
                  <textarea
                    rows={4}
                    value={form.project_brief}
                    onChange={(e) =>
                      setForm({ ...form, project_brief: e.target.value })
                    }
                    placeholder="What's your business, who do you serve, what makes you interesting to feature?"
                    data-testid="pg-brief"
                    className="w-full bg-transparent text-white outline-none text-sm resize-none placeholder:text-white/30"
                  />
                </Field>

                {/* Optional headshot / logo upload — only enabled once we
                    have a signed public token (i.e. valid email above). */}
                {uploadToken && (
                  <div
                    className="rounded-2xl bg-white/[0.02] border border-white/10 p-4"
                    data-testid="pg-media"
                  >
                    <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2">
                      Optional · Headshot + logo
                    </div>
                    <div className="text-[11px] text-white/60 mb-3 leading-relaxed">
                      Send us a headshot and your logo (or brand mark). It
                      makes the episode graphics + share cards go up faster.
                    </div>
                    <MediaUploader
                      ownerType="guest_request"
                      ownerId={form.email.trim().toLowerCase()}
                      authMode="public"
                      publicToken={uploadToken}
                      accept="image/*,application/pdf"
                      maxFiles={5}
                      compact
                      title=""
                      emptyLabel=""
                      testIdPrefix="pg-media"
                      onChange={(next) => setUploadedMedia(next)}
                    />
                  </div>
                )}

                {error && (
                  <div
                    data-testid="pg-error"
                    className="text-xs text-[#ff5577] bg-[#ff0055]/10 border border-[#ff0055]/30 rounded-xl px-3 py-2"
                  >
                    {error}
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div className="text-center py-6" data-testid="pg-step-done">
                <div className="mx-auto w-16 h-16 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/40 flex items-center justify-center mb-5">
                  <CheckCircle2 size={28} className="text-[#22c55e]" />
                </div>
                <h4 className="font-display font-black text-2xl tracking-tight">
                  Application sent.
                </h4>
                <p className="text-[#a395c3] text-sm mt-2 max-w-md mx-auto leading-relaxed">
                  Your Saturday slot is held in your name while we review.
                  Confirmation email coming to <span className="text-white">{form.email}</span>.
                </p>

                <div className="mt-6 max-w-md mx-auto text-left rounded-2xl bg-white/[0.03] border border-white/10 p-5 space-y-3">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#00f0ff]">
                    What happens next
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <span className="w-5 h-5 rounded-full bg-[#00f0ff]/15 text-[#00f0ff] text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                    <div className="text-white/85">
                      Our concierge verifies your social actions (usually within
                      24 hours).
                    </div>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <span className="w-5 h-5 rounded-full bg-[#00f0ff]/15 text-[#00f0ff] text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                    <div className="text-white/85">
                      You get a confirmation email with a calendar invite for
                      your Saturday slot.
                    </div>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <span className="w-5 h-5 rounded-full bg-[#00f0ff]/15 text-[#00f0ff] text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                    <div className="text-white/85">
                      Show up, record an amazing episode, become part of the SB
                      Spotlight family.
                    </div>
                  </div>
                </div>

                {bookingId && (
                  <a
                    href={`/booking/${bookingId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-6 inline-flex items-center gap-2 text-sm text-[#00f0ff] hover:text-white transition-colors"
                    data-testid="pg-track-link"
                  >
                    Track your application →
                  </a>
                )}
              </div>
            )}
          </div>

          {step < 4 && (
            <div className="flex items-center justify-between gap-3 px-6 md:px-7 py-5 bg-[#0a0612] border-t border-white/5 rounded-b-3xl">
              {/* Honeypot — bots often fill anything named "website" */}
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                aria-hidden="true"
                data-testid="pg-hp-website"
                style={{
                  position: "absolute",
                  left: "-9999px",
                  width: 1,
                  height: 1,
                  opacity: 0,
                }}
              />
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0}
                data-testid="pg-back"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm text-white/80 hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={14} /> Back
              </button>
              {step < 3 ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={!canAdvance()}
                  data-testid="pg-next"
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#ff0055] text-white font-semibold text-sm hover:bg-white hover:text-[#05050a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Continue <ChevronRight size={14} />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={!canAdvance() || submitting}
                  data-testid="pg-submit"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#ff0055] text-white font-semibold text-sm hover:bg-white hover:text-[#05050a] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Sending…
                    </>
                  ) : (
                    <>
                      Apply for guest spot <ChevronRight size={14} />
                    </>
                  )}
                </button>
              )}
            </div>
          )}
          {step === 4 && (
            <div className="flex items-center justify-end gap-3 px-7 py-5 bg-[#0a0612] border-t border-white/5 rounded-b-3xl">
              
              <button
                onClick={onClose}
                data-testid="pg-done-close"
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-white text-[#05050a] font-semibold text-sm hover:bg-[#00f0ff] transition-colors"
              >
                Close
              </button>
            </div>
          )}
        </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

const Field = ({ icon: Icon, label, required, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-1.5 flex items-center gap-2">
      <Icon size={11} />
      {label}
      {required && <span className="text-[#ff0055]">·</span>}
    </div>
    <div className="rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 focus-within:border-[#ff0055]/60 transition-colors">
      {children}
    </div>
  </div>
);

export default PodcastGuestModal;
